import { print } from "graphql";
import { Box, Button, Container, Image, Stack, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "../hooks/translation-hook";
import { requestGraphql } from "../queries/graphql-client";
import { heroImageQuery } from "../queries/queries";
import { texts } from "../texts/texts";

const IMAGE_BASE = "https://promaster-publish.panasonic.divid.se/files/f83bb8f8-030a-4df2-ba4e-5ad57aa41ff1/blob_";

type HeroImageResponse = {
  readonly products?: ReadonlyArray<{
    readonly id?: string | null;
    readonly name?: string | null;
    readonly modules?: {
      readonly images?: {
        readonly image?: ReadonlyArray<{
          readonly image?: string | null;
          readonly name?: string | null;
        }>;
      };
    };
  }>;
};

async function fetchHeroImageUrl(): Promise<string | null> {
  const data = await requestGraphql<HeroImageResponse, Record<string, never>>(print(heroImageQuery), {});
  const rootProduct = data.products?.find((product) => (product.name ?? "").trim().toLowerCase() === "root");
  const images = rootProduct?.modules?.images?.image ?? [];
  const heroImage = images.find((item) => (item.name ?? "").trim().toLowerCase() === "hero_image");
  const blobId = (heroImage?.image ?? "").trim();

  return blobId.length > 0 ? `${IMAGE_BASE}${blobId}` : null;
}

export function HeroBanner(): JSX.Element {
  const translate = useTranslation();
  const heroImageQueryResult = useQuery({
    queryKey: ["hero-image"],
    queryFn: fetchHeroImageUrl,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const heroImageUrl = heroImageQueryResult.data;

  return (
    <Box
      style={{
        position: "relative",
        background: "linear-gradient(135deg, #1a2744 0%, #2d6a9f 100%)",
        minHeight: 520,
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {heroImageUrl && (
        <Image
          src={heroImageUrl}
          alt="Hero image"
          fit="cover"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
          }}
        />
      )}

      <Box
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 100%)",
          zIndex: 0,
        }}
      />

      <Container size="xl" style={{ position: "relative", zIndex: 1 }} py="4rem">
        <Stack gap="xl" maw={580}>
          <Title order={1} c="white" fw={700} size="2.5rem" lh={1.2}>
            {translate(texts.hero_heading)}
          </Title>
          <Box style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Button
              component="a"
              href="#residential"
              variant="outline"
              size="sm"
              radius="xs"
              tt="uppercase"
              styles={{
                root: {
                  borderColor: "white",
                  color: "white",
                  "&:hover": { background: "rgba(255,255,255,0.12)" },
                },
              }}
            >
              {translate(texts.residential_cta)}
            </Button>
            <Button
              component="a"
              href="#commercial"
              variant="outline"
              size="sm"
              radius="xs"
              tt="uppercase"
              styles={{
                root: {
                  borderColor: "white",
                  color: "white",
                  "&:hover": { background: "rgba(255,255,255,0.12)" },
                },
              }}
            >
              {translate(texts.commercial_cta)}
            </Button>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
